import React, { FC, useEffect, useState } from "react";
import { DEF_PG_SIZE_SUB } from '../../constants/maximum-page-size';
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { useLocale } from "../../contexts/localeContext";
import { TableContextProvider } from "../../contexts/table/TableContextProvider";
import columns from '../../models/ColumnDefinitions/AccessGroupActionColumns';
import accessGroupDeviceActionColumns from "../../models/ColumnDefinitions/AccessGroupDeviceActionColumn";
import { ListData } from "../../models/ListData";
import { ListQuery } from "../../models/ListQuery";
import { Lookup } from "../../models/Lookup";
import LookupTypes from "../../models/LookupTypes";
import { TabSummary } from "../../models/TabSummary";
import searchColumns from '../../models/search/AccessGroupActionSearch';
import SiteSearch from "../../models/search/SiteSearch";
import { deleteAccessGroupAction, getAccessGroupActions, getDeviceActionsForAccessGroups, getDeviceActionsForAccessGroupsNamesWithPagination, storeAccessGroupAction } from "../../services/accessGroupActionService";
import { getSiteNamesWithPagination } from "../../services/siteService";
import { getErrorMessage } from "../../services/utilities/ServiceError";
import { appendFilter } from "../../utilities/filters";
import ActivityFrame from "../activity-frame/activity-frame";
import { useDialog } from "../dialog/DialogContext";
import TableTab from "../tabs/table-tab";
import { AccessGroupAction } from "../../models/AccessGroupAction";
import AssignModalTwo from "../assignWizard/AssignModal";
import AccessGroupDeviceActionSearch from "../../models/search/AccessGroupDeviceActionSearch";
import { Button } from '../button/Button';
import { toast } from "react-toastify";
import { PaginationModel } from "@/models/Pagination";

interface Props {
    tabs: TabSummary[];
    setTabs: (tabs: TabSummary[]) => void;
    itemId: string;
    siteId?: number;
    reloadPage: () => void;
    isFloorGroup?: boolean;
}

const AccessGroupEntryActions: FC<Props> = ({ tabs, setTabs, itemId, siteId, reloadPage, isFloorGroup}) => {
    const { translate } = useLocale();
    const title = translate.byKey(isFloorGroup ? TranslationKeys.assignedActions : TranslationKeys.assignedEntryActions);
    const [busy, setBusy] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const dialog = useDialog();
    const [entryActions, setEntryActions] = useState<AccessGroupAction[]>([]);
    const updateActionMessage = translate.byKey(isFloorGroup ? TranslationKeys.actionsUpdated : TranslationKeys.entryActionsUpdated);

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    useEffect(() => {
        dataRefresh((query) => getAssignedEntryActions(query));
    }, [itemId])

    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ]

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>) => {
        try {
            setBusyLoader(true);
            let pageNumber = 1, pagination: PaginationModel, deviceActionIds: string[] = [], rows: any[] = [];

            do {
                const entryActions = await getAssignedEntryActions({ pageSize: 100, goToPage: pageNumber });
                deviceActionIds = deviceActionIds.concat(entryActions?.rows.map((entryAction) => entryAction.deviceId.toString()));
                rows = rows.concat(entryActions.rows);
                pagination = entryActions.pagination;
                pageNumber++;
            } while(pageNumber <= pagination.totalPages)

            setEntryActions(rows);
            setSelectedIds(deviceActionIds);
            setSelectedRows(rows);
            setOriginalSelectedIds(deviceActionIds); // helps us check what was changed
            setOriginalSelectedRows(rows);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        } finally {
            setBusyLoader(false);
        }
    };

    const getAssignedEntryActions = (listParameters) => {
        return getAccessGroupActions(itemId, "0", listParameters);
    };

    const handleSubmit = async (toBeAssigned: string[], toBeUnassigned: string[]) => {
        const entryActionsIdsToBeRemoved = entryActions.filter(action => toBeUnassigned.includes(action.deviceId.toString())).map(action => action.id);

        entryActionsIdsToBeRemoved.length > 0 &&
            entryActionsIdsToBeRemoved
                .filter((id) => id.toString() !== "")
                .map(async (id) => {
                    setBusy(true);
                    await deleteAccessGroupAction(id.toString())
                        .then(() => {
                            toast.success(updateActionMessage);
                            reloadPage();
                        })
                        .catch((err) => {
                            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(err) });
                        })
                        .finally(() => {
                            setBusy(false);
                        });
                });

        toBeAssigned.length > 0 && toBeAssigned.map((id) => {
            setBusy(true)
            storeAccessGroupAction({
                agaDirection: "0",
                accessGroupId: itemId,
                devActionId: id,
            })
            .then(() => {
                toast.success(updateActionMessage);
                reloadPage();
            })
            .catch((err) => {
                dialog.pushError({title: translate.byKey(TranslationKeys.error), message: getErrorMessage(err)});
            }).finally(() => {
                setBusy(false);
            })
        })
    };

    return <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
        <TableContextProvider
            dataMethod={getAssignedEntryActions}
            tableDescriptor={columns}
            searchDescriptor={searchColumns}
            useTable
            usePagination
            useFilter
            useSearch
            useSort
            manualDataRefresh={manualDataRefresh}
            maxPageSize={DEF_PG_SIZE_SUB}
            lookup={lookup}>
            <TableTab
                title={title}
                selectRows={false}
                cyName={isFloorGroup ? "accessGroupEntryActions" : "accessGroupActions" }>
                <Button variant="submit" shape="rounded" className="py-1 px-5" onClick={() => setShowAddModal(true)}>{translate.byKey(TranslationKeys.manage)}</Button>
            </TableTab>
        </TableContextProvider>
        <AssignModalTwo
            isOpen={showAddModal}
            dataMethod={(listParameters?: ListQuery) => getDeviceActionsForAccessGroups({ ...listParameters, filters: appendFilter(siteId, 'Site', 'deviceImprox.site.id', listParameters?.filters ?? []) })}
            lookup={lookup}
            modalTitle={translate.byKey(isFloorGroup ? TranslationKeys.assignActions : TranslationKeys.assignEntryActions)}
            onClose={() => {setShowAddModal(false) }}
            assigningTitle={translate.byKey(isFloorGroup ? TranslationKeys.actions : TranslationKeys.entryActions)}
            tableColumns={accessGroupDeviceActionColumns}
            searchColumns={AccessGroupDeviceActionSearch}
            handleSubmit={handleSubmit}
            getAssignedItems={getAssignedItems}
            idPath={"deviceId"}/>
    </ActivityFrame>
}

export default AccessGroupEntryActions
